import React, { useMemo, useState } from 'react';
import { Form, Formik } from 'formik';
import { eJournalEntryPaymentMethod } from '../../types/accounting/eJournalEntryPaymentMethod';
import { eJournalEntryType } from '../../types/accounting/eJournalEntryType';
import { EditJournalEntryModal } from './EditJournalEntryModal';
import { Header3Text, Header4Text } from 'ui-toolkit';
import { createJournalEntryAsync } from '../../API/accounting/createJournalEntryAsync';
import { IOrderOption } from '../../types/accounting/IOrderOption';
import { useEscrowFileByOrderId } from '../../store/accounting/useEscrowFileByOrderId';
import { newJournalEntrySchema } from '../../store/accounting/newJournalEntrySchema';
import {
  createDefaultFetch,
  createFetchedSuccess,
  createFetchedWithError,
  createFetchInProgress,
} from 'types/rest/IFetchStatus';

export interface INewJournalEntryFormValues {
  paymentMethod?: eJournalEntryPaymentMethod;
  orderId?: string;
  amountCents?: number;
  entryType?: eJournalEntryType;
  note?: string;
  initiatedAtDate?: string;
  completedAtDate?: string;
}

interface INewJournalEntryProps {
  onClose: () => void;
  onSuccess: () => void;
}

export const NewJournalEntryModalContainer: React.FC<INewJournalEntryProps> = ({ onClose, onSuccess }) => {
  const [createdStatus, setCreatedStatus] = useState(createDefaultFetch());
  const [order, setOrder] = useState<IOrderOption | undefined>(undefined);
  const { fetchError, data: escrowFile } = useEscrowFileByOrderId(order && order.data.id);
  const createJournalEntry = async (values: INewJournalEntryFormValues) => {
    setCreatedStatus(createFetchInProgress());
    try {
      await createJournalEntryAsync({
        amountCents: values.amountCents!,
        entryType: values.entryType!,
        note: values.note!,
        paymentMethod: values.paymentMethod!,
        escrowFileId: escrowFile ? escrowFile.id : undefined,
        initiatedAtDate: values.initiatedAtDate!,
        completedAtDate: values.completedAtDate,
      });
      setCreatedStatus(createFetchedSuccess());
    } catch (fetchError) {
      setCreatedStatus(createFetchedWithError());
    }
  };

  return (
    <Formik
      validateOnMount
      validationSchema={newJournalEntrySchema}
      initialValues={useMemo(() => {
        return {} as INewJournalEntryFormValues;
      }, [])}
      onSubmit={createJournalEntry}
    >
      {({ isValid, submitForm }) => (
        <Form>
          <EditJournalEntryModal
            title={<Header4Text>New Journal Entry</Header4Text>}
            errorTitle={<Header3Text>Error creating new Journal Entry</Header3Text>}
            loadingTitle="Creating Journal Entry..."
            successTitle="New Journal Entry was created."
            saveButtonText="Save and Add"
            disabledFields={[]}
            escrowFile={escrowFile}
            escrowFileError={fetchError}
            fetchStatus={createdStatus}
            isValid={isValid}
            onClose={onClose}
            onSuccess={onSuccess}
            onOrderSelected={setOrder}
            selectedOrder={order}
            submitForm={submitForm}
          />
        </Form>
      )}
    </Formik>
  );
};
